import React, { useEffect, useMemo, useState } from "react";
import { Box, Modal, CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";
import {
  useTeamBoard,
  useTeamBoardMutations,
  TEAM_BOARD_TASKS_KEY,
  TEAM_LEAD_TASKS_KEY,
  teamBoardKey,
} from "../../../hooks/useTeamBoards";
import { useEffectivePermissions, scopeSetFromPermissions } from "../../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../../context/AuthContext";
import { canManageTeamBoard, canExecuteTeamTask } from "../utils/teamBoardPermissions";
import { useTeamBoardAction } from "../hooks/useTeamBoardAction";
import TaskCardModal from "./TaskCardModal";

/**
 * Modal de detalhe de tarefa unificado (inbox, kanban, chat/solicitação, etc.).
 */
const TaskCardModalContainer = ({
  open,
  card,
  board: boardProp = null,
  boardId: boardIdProp = null,
  onClose,
  extraCacheKeys = [],
  contextLeadId: contextLeadIdProp = null,
  invalidateInboxOnClose = false,
  closeOnSave = true,
  closeOnDelete = true,
  onCardChange = null,
}) => {
  const queryClient = useQueryClient();
  const { run } = useTeamBoardAction();
  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const scopes = scopeSetFromPermissions(permissionsData, userData);

  const [overrideCard, setOverrideCard] = useState(null);

  useEffect(() => {
    if (!open) setOverrideCard(null);
  }, [open]);

  useEffect(() => {
    setOverrideCard(null);
  }, [card?.id]);

  const activeCard = overrideCard || card;
  const resolvedBoardId = boardProp?.id ?? boardIdProp ?? activeCard?.boardId ?? null;

  const { data: fetchedBoard, isLoading: boardLoading } = useTeamBoard(resolvedBoardId, {
    enabled: Boolean(open && resolvedBoardId && !boardProp),
    contextLeadId: contextLeadIdProp || null,
  });

  const board = boardProp ?? fetchedBoard ?? null;
  const boardId = board?.id;
  const columns = board?.columns || [];
  const contextLeadId =
    contextLeadIdProp != null
      ? Number(contextLeadIdProp)
      : board?.contextLeadId != null
        ? Number(board.contextLeadId)
        : board?.filterByLead && board?.leadId != null
          ? Number(board.leadId)
          : null;

  const mutations = useTeamBoardMutations(boardId, {
    extraCacheKeys,
    contextLeadId: contextLeadId || null,
  });

  const liveCard = useMemo(() => {
    if (!activeCard?.id) return null;
    for (const col of columns) {
      const found = (col.cards || []).find((c) => Number(c.id) === Number(activeCard.id));
      if (found) return { ...found, columnId: col.id };
    }
    return activeCard;
  }, [columns, activeCard]);

  const resolvedCard = liveCard ?? activeCard;

  const canManage = useMemo(() => canManageTeamBoard(scopes, board), [scopes, board]);
  const canExecute = useMemo(
    () => canExecuteTeamTask(scopes, board, resolvedCard, userData?.id),
    [scopes, board, resolvedCard, userData?.id]
  );

  const invalidateInbox = () => {
    if (invalidateInboxOnClose) {
      queryClient.invalidateQueries(TEAM_BOARD_TASKS_KEY);
    }
    queryClient.invalidateQueries(TEAM_LEAD_TASKS_KEY);
  };

  const handleClose = () => {
    invalidateInbox();
    onClose?.();
  };

  const handleSave = async (payload) => {
    const result = await run(
      () => mutations.updateCard.mutateAsync(payload),
      { context: "saveCard", successKey: "successCardSaved" }
    );
    if (result === null) return false;
    if (boardId) queryClient.invalidateQueries(teamBoardKey(boardId));
    invalidateInbox();
    if (closeOnSave) onClose?.();
    return true;
  };

  const handleDelete = async (cardId) => {
    const result = await run(
      () => mutations.deleteCard.mutateAsync(cardId),
      { context: "deleteCard", successKey: "successCardDeleted" }
    );
    if (result === null) return false;
    invalidateInbox();
    if (closeOnDelete) onClose?.();
    return true;
  };

  const handleMoveColumn = async (columnId) => {
    if (!resolvedCard?.id) return;
    const result = await run(
      () =>
        mutations.moveCard.mutateAsync({
          cardId: resolvedCard.id,
          columnId,
          position: 0,
        }),
      { context: "moveCard" }
    );
    if (result === null) return;
    invalidateInbox();
  };

  const handleSelectCard = (link) => {
    if (!link?.id) return;
    for (const col of columns) {
      const found = (col.cards || []).find((c) => Number(c.id) === Number(link.id));
      if (found) {
        const next = { ...found, columnId: col.id };
        setOverrideCard(next);
        onCardChange?.(next);
        return;
      }
    }
  };

  if (open && boardLoading && !boardProp) {
    return (
      <Modal open>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      </Modal>
    );
  }

  if (!board || !resolvedCard) return null;

  return (
    <TaskCardModal
      open={open}
      card={resolvedCard}
      board={board}
      onClose={handleClose}
      onSave={handleSave}
      onDelete={handleDelete}
      onMoveColumn={handleMoveColumn}
      canManage={canManage}
      canExecute={canExecute}
      cardMutations={mutations}
      onSelectCard={handleSelectCard}
    />
  );
};

export default TaskCardModalContainer;
